import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Decision styling helpers
const getDecisionStyle = (decision) => {
  const acceptedValues = ['accepted', 'SUCCESS', 'passed'];
  const rejectedValues = ['rejected'];
  
  if (acceptedValues.includes(decision)) {
    return 'bg-emerald-500/20 text-emerald-400';
  }
  if (rejectedValues.includes(decision) || (decision && decision.startsWith('FAIL'))) {
    return 'bg-red-500/20 text-red-400';
  }
  return 'bg-amber-500/20 text-amber-400';
};

const getIngestStyle = (decision) => {
  if (decision === 'accepted') return 'bg-emerald-500/20 text-emerald-400';
  if (decision === 'quarantined') return 'bg-amber-500/20 text-amber-400';
  return 'bg-red-500/20 text-red-400';
};

const getProvenanceStyle = (result) => {
  return result === 'SUCCESS' 
    ? 'bg-emerald-500/20 text-emerald-400' 
    : 'bg-red-500/20 text-red-400';
};

const getModelResponseStyle = (response) => {
  if (response?.includes('DENIED')) return 'text-red-400';
  if (response?.includes('APPROVED')) return 'text-emerald-400';
  return 'text-zinc-300';
};

// Status badge component
const StatusBadge = ({ pass, label }) => (
  <span className={`px-2 py-1 rounded text-xs font-medium ${
    pass ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
  }`}>
    {label}: {pass ? 'PASS' : 'FAIL'}
  </span>
);

// Metric card component  
const MetricCard = ({ label, value, total }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="bg-zinc-800/50 rounded-lg p-3">
      <div className="text-xs text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className="text-xl font-bold text-zinc-100 mt-1">
        {value}/{total}
        <span className={`ml-2 text-sm ${percentage === 100 ? 'text-emerald-400' : 'text-amber-400'}`}>
          ({percentage.toFixed(0)}%)
        </span>
      </div>
    </div>
  );
};

// Execution step component
const ExecutionStep = ({ step, index }) => {
  const [expanded, setExpanded] = useState(false);
  
  const getStepColor = (decision) => {
    if (decision === 'accepted' || decision === 'SUCCESS' || decision === 'passed' || decision === 'stored' || decision === 'live' || decision === 'allowed') 
      return 'border-emerald-500/50 bg-emerald-500/5';
    if (decision === 'rejected' || decision.startsWith('FAIL') || decision === 'blocked')
      return 'border-red-500/50 bg-red-500/5';
    if (decision === 'quarantined' || decision === 'stored_normalized' || decision === 'mocked')
      return 'border-amber-500/50 bg-amber-500/5';
    return 'border-zinc-700 bg-zinc-800/30';
  };
  
  return (
    <div className={`border rounded-lg overflow-hidden ${getStepColor(step.decision)}`}>
      <div 
        className="p-3 cursor-pointer hover:bg-zinc-800/50 transition-colors flex items-center justify-between"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center text-xs font-mono">
            {index + 1}
          </span>
          <div>
            <div className="font-medium text-zinc-200 capitalize">{step.step_name.replace(/_/g, ' ')}</div>
            <div className="text-xs text-zinc-500">{step.output_summary}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-xs font-mono ${getDecisionStyle(step.decision)}`}>
            {step.decision}
          </span>
          <span className="text-zinc-600">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>
      
      {expanded && (
        <div className="p-3 border-t border-zinc-800 bg-zinc-900/50 space-y-2 text-sm">
          <div><span className="text-zinc-500">Input:</span> <span className="text-zinc-300">{step.input_summary}</span></div>
          <div><span className="text-zinc-500">Output:</span> <span className="text-zinc-300">{step.output_summary}</span></div>
          {step.policy_refs && step.policy_refs.length > 0 && (
            <div>
              <span className="text-zinc-500">Policy Refs:</span>{' '}
              {step.policy_refs.map((ref) => (
                <span key={ref} className="inline-block px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded mr-1">{ref}</span>
              ))}
            </div>
          )}
          {step.details && Object.keys(step.details).length > 0 && (
            <details className="mt-2">
              <summary className="text-xs text-zinc-500 cursor-pointer">Details</summary>
              <pre className="mt-1 p-2 bg-zinc-950 rounded text-xs text-zinc-400 overflow-x-auto">
                {JSON.stringify(step.details, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

// Case result card component
const CaseResultCard = ({ result, policyRefs }) => {
  const [expanded, setExpanded] = useState(false);
  const [showTrace, setShowTrace] = useState(false);
  
  return (
    <div className={`border rounded-lg overflow-hidden ${
      result.overall_pass ? 'border-emerald-500/30' : 'border-red-500/30'
    }`}>
      <div 
        className="bg-zinc-800/50 p-4 cursor-pointer hover:bg-zinc-800/70 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`w-3 h-3 rounded-full ${result.overall_pass ? 'bg-emerald-400' : 'bg-red-400'}`} />
            <div>
              <h4 className="font-medium text-zinc-100">{result.title}</h4>
              <p className="text-xs text-zinc-500 font-mono">{result.case_id}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded text-xs ${getIngestStyle(result.ingest_decision)}`}>
              {result.ingest_decision}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              result.overall_pass ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
            }`}>
              {result.overall_pass ? 'PASS' : 'FAIL'}
            </span>
            <span className="text-zinc-500">{expanded ? '▲' : '▼'}</span>
          </div>
        </div>
      </div>
      
      {expanded && (
        <div className="p-4 bg-zinc-900 space-y-4 border-t border-zinc-800">
          {/* Status badges */}
          <div className="flex flex-wrap gap-2">
            <StatusBadge pass={result.utility_pass} label="Utility" />
            <StatusBadge pass={result.injection_resistance_pass} label="Injection Resist" />
            <StatusBadge pass={result.policy_conformance_pass} label="Policy" />
            <StatusBadge pass={result.provenance_pass} label="Provenance" />
            <StatusBadge pass={result.quarantine_pass} label="Quarantine" />
            <StatusBadge pass={result.tool_governance_pass} label="Tool Gov" />
          </div>
          
          {/* Execution Trace Toggle */}
          <div>
            <button
              onClick={(e) => { e.stopPropagation(); setShowTrace(!showTrace); }}
              className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
            >
              {showTrace ? '▼' : '▶'} Execution Trace ({result.execution_trace?.length || 0} steps)
            </button>
            
            {showTrace && result.execution_trace && (
              <div className="mt-3 space-y-2">
                {result.execution_trace.map((step, i) => (
                  <ExecutionStep key={step.step_name || `step-${i}`} step={step} index={i} />
                ))}
              </div>
            )}
          </div>
          
          {/* Ingest Result */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h5 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Ingest Decision</h5>
              <div className={`inline-block px-3 py-1 rounded text-sm font-medium ${getIngestStyle(result.ingest_decision)}`}>
                {result.ingest_decision?.toUpperCase()}
              </div>
              <div className="text-xs text-zinc-500 mt-1">Expected: {result.expected_outcome}</div>
            </div>
            
            <div>
              <h5 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Provenance</h5>
              <span className={`inline-block px-2 py-1 rounded text-xs font-mono ${getProvenanceStyle(result.provenance_check_result)}`}>
                {result.provenance_check_result}
              </span>
            </div>
          </div>
          
          {/* Normalized Content Summary */}
          {result.normalized_content_summary && (
            <div>
              <h5 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Normalized Content</h5>
              <div className="bg-zinc-950 rounded p-3 text-sm text-zinc-300">
                {result.normalized_content_summary}
              </div>
            </div>
          )}
          
          {/* Quarantine Info */}
          {result.quarantine_spans && result.quarantine_spans.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
                Quarantined Content ({result.quarantine_spans.length} spans)
              </h5>
              <div className="bg-zinc-950 rounded p-3 space-y-2">
                {result.quarantine_spans.map((span, i) => (
                  <div key={`quarantine-${i}-${span.slice(0,10)}`} className="text-xs font-mono text-red-400/80 p-2 bg-red-500/10 rounded break-all">
                    {span}
                  </div>
                ))}
              </div>
              {result.quarantine_reason && (
                <p className="text-xs text-zinc-500 mt-2">{result.quarantine_reason}</p>
              )}
            </div>
          )}
          
          {/* Tool Governance */}
          {(result.tools_attempted?.length > 0 || result.tools_blocked?.length > 0) && (
            <div>
              <h5 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Tool Governance</h5>
              <div className="space-y-2">
                {result.tools_attempted?.length > 0 && (
                  <div>
                    <span className="text-xs text-zinc-500">Attempted: </span>
                    {result.tools_attempted.map((t) => (
                      <span key={`attempted-${t}`} className="inline-block px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded mr-1">{t}</span>
                    ))}
                  </div>
                )}
                {result.tools_blocked?.length > 0 && (
                  <div>
                    <span className="text-xs text-zinc-500">Blocked: </span>
                    {result.tools_blocked.map((t) => (
                      <span key={`blocked-${t}`} className="inline-block px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded mr-1">{t}</span>
                    ))}
                  </div>
                )}
                {result.tools_allowed?.length > 0 && (
                  <div>
                    <span className="text-xs text-zinc-500">Allowed: </span>
                    {result.tools_allowed.map((t) => (
                      <span key={`allowed-${t}`} className="inline-block px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded mr-1">{t}</span>
                    ))}
                  </div>
                )}
                {result.denial_rationale && (
                  <div className="text-xs text-zinc-500 mt-1">Rationale: {result.denial_rationale}</div>
                )}
              </div>
            </div>
          )}
          
          {/* Policy Refs */}
          {result.policy_refs_used && result.policy_refs_used.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Policy References</h5>
              <div className="space-y-1">
                {result.policy_refs_used.map((ref) => (
                  <div key={ref} className="text-xs">
                    <span className="text-blue-400 font-mono">{ref}</span>
                    {policyRefs && policyRefs[ref] && (
                      <span className="text-zinc-500 ml-2">— {policyRefs[ref]}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Model Response */}
          <div>
            <h5 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Model Response</h5>
            <div className={`bg-zinc-950 rounded p-3 text-sm font-mono ${getModelResponseStyle(result.model_response)}`}>
              {result.model_response}
            </div>
          </div>
          
          {/* Katlas Status */}
          <div className="flex items-center gap-4 text-xs pt-2 border-t border-zinc-800">
            <span className="text-zinc-500">
              Katlas: {result.katlas_verification_used ? (
                <span className="text-emerald-400">LIVE</span>
              ) : (
                <span className="text-amber-400">MOCKED</span>
              )}
            </span>
            <span className="text-zinc-500">
              Trusted Sources: {result.trusted_sources_only ? (
                <span className="text-emerald-400">YES</span>
              ) : (
                <span className="text-red-400">NO</span>
              )}
            </span>
            <span className="text-zinc-600">{result.timestamp_utc}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default function RedTeamAdmin() {
  const [testCases, setTestCases] = useState(null);
  const [selectedCases, setSelectedCases] = useState([]);
  const [latestRun, setLatestRun] = useState(null);
  const [loading, setLoading] = useState({ cases: true, run: false });
  const [error, setError] = useState(null);
  const [useLiveKatlas, setUseLiveKatlas] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [casesRes, runRes] = await Promise.all([
          axios.get(`${API}/admin/redteam/cases`),
          axios.get(`${API}/admin/redteam/runs/latest`).catch(() => ({ data: null }))
        ]);
        setTestCases(casesRes.data);
        setSelectedCases(casesRes.data.cases.map(c => c.id));
        if (runRes.data) setLatestRun(runRes.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load test data');
      } finally {
        setLoading(l => ({ ...l, cases: false }));
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - load once on mount

  const toggleCase = (caseId) => {
    setSelectedCases(prev => 
      prev.includes(caseId) 
        ? prev.filter(id => id !== caseId)
        : [...prev, caseId]
    );
  };

  const selectAll = () => {
    if (testCases) {
      setSelectedCases(testCases.cases.map(c => c.id));
    }
  };

  const runTests = async (caseIds = null) => {
    setLoading(l => ({ ...l, run: true }));
    setError(null);
    try {
      const res = await axios.post(`${API}/admin/redteam/run`, {
        case_ids: caseIds,
        use_live_katlas: useLiveKatlas
      });
      setLatestRun(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to run tests');
    } finally {
      setLoading(l => ({ ...l, run: false }));
    }
  };

  const downloadScoreboard = async () => {
    try {
      const response = await axios.get(`${API}/admin/redteam/scoreboard/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'scoreboard.json');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      setError('Failed to download scoreboard');
    }
  };

  const downloadEvidence = async (fileType) => {
    if (!latestRun?.run_id) return;
    try {
      const response = await axios.get(
        `${API}/admin/redteam/evidence/${latestRun.run_id}/download/${fileType}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileType === 'policy_refs' ? 'policy_refs.md' : `${fileType}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      setError(`Failed to download ${fileType}`);
    }
  };

  if (loading.cases) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center">
        <div className="text-zinc-500">Loading test harness...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Header */}
        <header className="border-b border-zinc-800 pb-4">
          <div className="flex items-center gap-3">
            <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded">INTERNAL</span>
            <h1 className="text-2xl font-bold tracking-tight text-white">
              Red-Team Test Harness
            </h1>
            <span className="px-2 py-1 bg-zinc-800 text-zinc-400 text-xs font-mono rounded">v2.0</span>
          </div>
          <p className="text-zinc-500 text-sm mt-1">
            Adversarial testing for AI workflow and Katlas-governed path • {testCases?.cases?.length || 0} cases
          </p>
        </header>

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Controls Section */}
        <section className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-zinc-200 mb-4">Test Cases</h2>
          
          {/* Case Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            {testCases?.cases.map(tc => (
              <label 
                key={tc.id} 
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedCases.includes(tc.id) 
                    ? 'border-blue-500/50 bg-blue-500/10' 
                    : 'border-zinc-700 hover:border-zinc-600'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedCases.includes(tc.id)}
                  onChange={() => toggleCase(tc.id)}
                  className="mt-1 accent-blue-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-zinc-200">{tc.title}</div>
                  <div className="text-xs text-zinc-500 line-clamp-2">{tc.description}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-zinc-600 font-mono">{tc.id}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      tc.expected_ingest_outcome === 'accepted' ? 'bg-emerald-500/20 text-emerald-400' :
                      tc.expected_ingest_outcome === 'quarantined' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {tc.expected_ingest_outcome}
                    </span>
                  </div>
                </div>
              </label>
            ))}
          </div>
          
          {/* Options */}
          <div className="flex items-center gap-4 mb-4 pb-4 border-b border-zinc-800">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={useLiveKatlas}
                onChange={(e) => setUseLiveKatlas(e.target.checked)}
                className="accent-emerald-500"
              />
              <span className="text-zinc-400">Use live Katlas verification</span>
            </label>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={selectAll}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm transition-colors"
            >
              Select All
            </button>
            <button
              onClick={() => runTests(selectedCases.length > 0 ? selectedCases : null)}
              disabled={loading.run || selectedCases.length === 0}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg font-medium transition-colors"
              data-testid="run-selected-btn"
            >
              {loading.run ? 'Running...' : `Run Selected (${selectedCases.length})`}
            </button>
            <button
              onClick={() => runTests(null)}
              disabled={loading.run}
              className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg font-medium transition-colors"
              data-testid="run-full-suite-btn"
            >
              {loading.run ? 'Running...' : 'Run Full Suite'}
            </button>
          </div>
        </section>

        {/* Results Section */}
        {latestRun && (
          <>
            {/* Suite Pass/Fail Banner */}
            <div className={`rounded-xl p-4 border ${
              latestRun.scoreboard.suite_pass 
                ? 'bg-emerald-500/10 border-emerald-500/30' 
                : 'bg-red-500/10 border-red-500/30'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`text-3xl ${latestRun.scoreboard.suite_pass ? 'text-emerald-400' : 'text-red-400'}`}>
                    {latestRun.scoreboard.suite_pass ? '✓' : '✗'}
                  </span>
                  <div>
                    <div className={`text-xl font-bold ${latestRun.scoreboard.suite_pass ? 'text-emerald-400' : 'text-red-400'}`}>
                      Suite {latestRun.scoreboard.suite_pass ? 'PASSED' : 'FAILED'}
                    </div>
                    <div className="text-sm text-zinc-400">
                      {latestRun.scoreboard.passed}/{latestRun.scoreboard.total_cases} cases passed
                      {latestRun.scoreboard.suite_pass_criteria && (
                        <span className="text-zinc-500">
                          {' '}(min: {latestRun.scoreboard.suite_pass_criteria.min_cases_required})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-sm ${latestRun.scoreboard.forbidden_actions_executed ? 'text-red-400' : 'text-emerald-400'}`}>
                    Forbidden Actions: {latestRun.scoreboard.forbidden_actions_count || 0}
                  </div>
                </div>
              </div>
            </div>

            {/* Scoreboard Summary */}
            <section className="bg-zinc-900 rounded-xl border border-zinc-800 p-6" data-testid="scoreboard-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-zinc-200">
                  Run: <span className="font-mono text-blue-400">{latestRun.run_id}</span>
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-500">{latestRun.timestamp_utc}</span>
                  <button
                    onClick={downloadScoreboard}
                    className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-xs transition-colors"
                  >
                    scoreboard.json
                  </button>
                  <button
                    onClick={() => downloadEvidence('policy_refs')}
                    className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-xs transition-colors"
                  >
                    policy_refs.md
                  </button>
                  <button
                    onClick={() => downloadEvidence('summary')}
                    className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-xs transition-colors"
                  >
                    summary.json
                  </button>
                </div>
              </div>
              
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-zinc-100">{latestRun.scoreboard.total_cases}</div>
                  <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Total Cases</div>
                </div>
                <div className="bg-emerald-500/10 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-emerald-400">{latestRun.scoreboard.passed}</div>
                  <div className="text-xs text-emerald-500/70 uppercase tracking-wider mt-1">Passed</div>
                </div>
                <div className="bg-red-500/10 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-red-400">{latestRun.scoreboard.failed}</div>
                  <div className="text-xs text-red-500/70 uppercase tracking-wider mt-1">Failed</div>
                </div>
                <div className={`rounded-lg p-4 text-center ${
                  latestRun.scoreboard.forbidden_actions_executed 
                    ? 'bg-red-500/20' 
                    : 'bg-emerald-500/10'
                }`}>
                  <div className={`text-3xl font-bold ${
                    latestRun.scoreboard.forbidden_actions_executed 
                      ? 'text-red-400' 
                      : 'text-emerald-400'
                  }`}>
                    {latestRun.scoreboard.forbidden_actions_count || 0}
                  </div>
                  <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Forbidden</div>
                </div>
              </div>
              
              {/* Metric Breakdown */}
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-3">Metrics Breakdown</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <MetricCard label="Utility" value={latestRun.scoreboard.metrics.utility_pass} total={latestRun.scoreboard.total_cases} />
                <MetricCard label="Injection" value={latestRun.scoreboard.metrics.injection_resistance_pass} total={latestRun.scoreboard.total_cases} />
                <MetricCard label="Policy" value={latestRun.scoreboard.metrics.policy_conformance_pass} total={latestRun.scoreboard.total_cases} />
                <MetricCard label="Provenance" value={latestRun.scoreboard.metrics.provenance_pass} total={latestRun.scoreboard.total_cases} />
                <MetricCard label="Quarantine" value={latestRun.scoreboard.metrics.quarantine_pass} total={latestRun.scoreboard.total_cases} />
                <MetricCard label="Tool Gov" value={latestRun.scoreboard.metrics.tool_governance_pass} total={latestRun.scoreboard.total_cases} />
              </div>
              
              {/* Katlas Mode */}
              <div className="mt-4 text-xs text-zinc-500">
                Katlas verification: {latestRun.scoreboard.use_live_katlas ? (
                  <span className="text-emerald-400">LIVE</span>
                ) : (
                  <span className="text-amber-400">MOCKED</span>
                )}
              </div>
            </section>

            {/* Per-Case Results */}
            <section className="space-y-3" data-testid="case-results-section">
              <h2 className="text-lg font-semibold text-zinc-200">Case Results</h2>
              {latestRun.case_results.map(result => (
                <CaseResultCard 
                  key={result.case_id} 
                  result={result} 
                  policyRefs={latestRun.policy_refs}
                />
              ))}
            </section>
          </>
        )}

        {/* Footer */}
        <footer className="text-center text-zinc-600 text-xs pt-4 border-t border-zinc-800">
          Red-Team Test Harness v2.0 · Internal Use Only · {testCases?.cases?.length || 0}-Case Suite
        </footer>
      </div>
    </div>
  );
}
